import {
  contactResponseSchema,
  featuresResponseSchema,
  loginResponseSchema,
  passwordResetConfirmResponseSchema,
  passwordResetRequestResponseSchema,
  type ContactRequest,
  type ContactResponse,
  type FeaturesResponse,
  type LoginRequest,
  type LoginResponse,
  type PasswordResetConfirm,
  type PasswordResetConfirmResponse,
  type PasswordResetRequest,
  type PasswordResetRequestResponse,
} from "@/contracts";
import type { z } from "zod";

import { ApiError, networkError, toApiError } from "./api-error";

export type Session = LoginResponse;

export type SessionListener = (session: Session | null) => void;

export interface RequestOptions<TResult> {
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  /** Validates the response against the shared contract before it reaches a screen. */
  schema?: z.ZodType<TResult>;
  /** Overrides the default `Accept: application/json` (binary downloads). */
  accept?: string;
}

export interface BlobDownload {
  blob: Blob;
  /** From Content-Disposition when the server sends one. */
  filename: string | null;
}

interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

/** Relative default — Vite's dev proxy (and any same-origin host) serves `/api`. */
const DEFAULT_BASE_URL = "/api/v1";

/**
 * Resolves the API base used by the default client.
 *
 * Locally leave `VITE_API_BASE_URL` unset so the browser calls `/api/v1` on the
 * Vite origin and the proxy forwards it (ADR-035). In production the Azure
 * Static Web App and App Service are different hosts, so the deploy bakes an
 * absolute URL such as `https://semakiot-monitor.azurewebsites.net/api/v1`.
 * A bare origin is accepted and `/api/v1` is appended.
 */
export function resolveApiBaseUrl(
  configured: string | undefined = import.meta.env.VITE_API_BASE_URL,
): string {
  const trimmed = configured?.trim();

  if (!trimmed) {
    return DEFAULT_BASE_URL;
  }

  const withoutTrailingSlash = trimmed.replace(/\/+$/, "");

  if (withoutTrailingSlash.endsWith("/api/v1")) {
    return withoutTrailingSlash;
  }

  return `${withoutTrailingSlash}/api/v1`;
}

/**
 * The single way this app talks to the API.
 *
 * Three rules it exists to enforce, all from section 3 of the master prompt:
 *
 * 1. The access token lives in this object and nowhere else. Never in
 *    `localStorage`, never in `sessionStorage`, so a cross-site script cannot
 *    read it back out and a closed tab ends the session.
 * 2. A 401 on an authenticated call triggers at most one refresh and at most
 *    one retry. Two attempts on the same request would be a loop waiting for
 *    a reason to happen.
 * 3. Concurrent 401s share one refresh. The refresh token rotates on every
 *    use and the API revokes a whole family when a rotated token is replayed,
 *    so firing three refreshes at once would log the user out as if they had
 *    been attacked.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  private session: Session | null = null;
  private refreshInFlight: Promise<Session | null> | null = null;
  private readonly listeners = new Set<SessionListener>();

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? resolveApiBaseUrl();
    // Bound because a bare `fetch` reference loses its receiver in some
    // browsers and in jsdom.
    this.fetchImpl = options.fetchImpl ?? ((...args) => globalThis.fetch(...args));
  }

  getSession(): Session | null {
    return this.session;
  }

  /** Notifies on login, refresh and every way a session ends. */
  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  async login(credentials: LoginRequest): Promise<Session> {
    const session = await this.send<LoginResponse>(
      "POST",
      "/auth/login",
      { body: credentials, schema: loginResponseSchema },
      { authenticated: false, allowRetry: false },
    );

    this.setSession(session);

    return session;
  }

  /** Always flat success — does not reveal whether the email exists (ADR-085). */
  requestPasswordReset(payload: PasswordResetRequest): Promise<PasswordResetRequestResponse> {
    return this.send<PasswordResetRequestResponse>(
      "POST",
      "/auth/password-reset/request",
      { body: payload, schema: passwordResetRequestResponseSchema },
      { authenticated: false, allowRetry: false },
    );
  }

  confirmPasswordReset(payload: PasswordResetConfirm): Promise<PasswordResetConfirmResponse> {
    return this.send<PasswordResetConfirmResponse>(
      "POST",
      "/auth/password-reset/confirm",
      { body: payload, schema: passwordResetConfirmResponseSchema },
      { authenticated: false, allowRetry: false },
    );
  }

  /** Public marketing form — no session, no refresh retry. */
  submitContact(payload: ContactRequest): Promise<ContactResponse> {
    return this.send<ContactResponse>(
      "POST",
      "/contact",
      { body: payload, schema: contactResponseSchema },
      { authenticated: false, allowRetry: false },
    );
  }

  /**
   * Public hardware visibility flags — no session.
   *
   * Used by marketing `/equipos` and the supported-devices catalogue so a
   * model can be rolled out one device at a time (FEATURE_FLAGS.enabledHardware).
   */
  getFeatures(): Promise<FeaturesResponse> {
    return this.send<FeaturesResponse>(
      "GET",
      "/features",
      { schema: featuresResponseSchema },
      { authenticated: false, allowRetry: false },
    );
  }

  /**
   * Recovers a session on a cold start using the refresh cookie alone.
   *
   * Reloading the page throws away the in-memory access token, which is the
   * point of keeping it there; without this the user would be sent to the
   * login form on every refresh even though their session is perfectly valid.
   * Returns null when there is nothing to recover, which is not an error.
   */
  async restore(): Promise<Session | null> {
    return this.refreshOnce();
  }

  /**
   * Ends the session locally whatever the server answers.
   *
   * If the call fails the cookie may survive on the server, but keeping the
   * user logged in on screen after they asked to leave is the worse outcome.
   */
  async logout(): Promise<void> {
    try {
      await this.send("POST", "/auth/logout", {}, { authenticated: false, allowRetry: false });
    } finally {
      this.setSession(null);
    }
  }

  async logoutAll(): Promise<void> {
    try {
      await this.send("POST", "/auth/logout-all", {}, { allowRetry: false });
    } finally {
      this.setSession(null);
    }
  }

  get<TResult>(path: string, options: RequestOptions<TResult> = {}): Promise<TResult> {
    return this.send<TResult>("GET", path, options);
  }

  /**
   * Authenticated binary download (PDF expediente copies, and similar).
   *
   * The access token stays in memory, so a plain `<a href>` cannot carry it —
   * this path fetches the blob and lets the caller trigger a save.
   */
  async getBlob(path: string, options: Omit<RequestOptions<unknown>, "schema"> = {}): Promise<BlobDownload> {
    const authenticated = true;
    const allowRetry = true;
    const hadToken = this.session !== null;
    const request = { ...options, accept: options.accept ?? "application/pdf" };

    let response = await this.dispatch("GET", path, request, authenticated);

    if (response.status === 401 && allowRetry && hadToken) {
      const refreshed = await this.refreshOnce();

      if (!refreshed) {
        throw await toApiError(response);
      }

      response = await this.dispatch("GET", path, request, authenticated);
    }

    if (!response.ok) {
      throw await toApiError(response);
    }

    return {
      blob: await response.blob(),
      filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")),
    };
  }

  post<TResult>(path: string, options: RequestOptions<TResult> = {}): Promise<TResult> {
    return this.send<TResult>("POST", path, options);
  }

  patch<TResult>(path: string, options: RequestOptions<TResult> = {}): Promise<TResult> {
    return this.send<TResult>("PATCH", path, options);
  }

  /**
   * The API answers a successful delete with 204, so there is nothing to
   * validate and callers get `void`. `parse` already returns early on 204,
   * which is why no schema is threaded through here.
   */
  delete(path: string): Promise<void> {
    return this.send<void>("DELETE", path, {});
  }

  private setSession(session: Session | null): void {
    this.session = session;

    for (const listener of this.listeners) {
      listener(session);
    }
  }

  private async send<TResult>(
    method: string,
    path: string,
    options: RequestOptions<TResult>,
    control: { authenticated?: boolean; allowRetry?: boolean } = {},
  ): Promise<TResult> {
    const authenticated = control.authenticated ?? true;
    const allowRetry = control.allowRetry ?? true;

    // Captured before the call so a concurrent refresh cannot make this look
    // like a request that never had a token.
    const hadToken = this.session !== null;

    const response = await this.dispatch(method, path, options, authenticated);

    if (response.status === 401 && allowRetry && authenticated && hadToken) {
      const refreshed = await this.refreshOnce();

      if (!refreshed) {
        throw await toApiError(response);
      }

      // Exactly one retry: a second 401 here means the new token is not the
      // problem, so raising it is more useful than trying again.
      const retried = await this.dispatch(method, path, options, authenticated);

      return this.parse(retried, options.schema);
    }

    return this.parse(response, options.schema);
  }

  private async dispatch<TResult>(
    method: string,
    path: string,
    options: RequestOptions<TResult>,
    authenticated: boolean,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: options.accept ?? "application/json",
    };

    if (options.body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    // Read at dispatch time, not at send time, so a retry uses the token the
    // refresh just produced.
    if (authenticated && this.session) {
      headers.Authorization = `Bearer ${this.session.accessToken}`;
    }

    try {
      return await this.fetchImpl(this.url(path, options.query), {
        method,
        headers,
        // The refresh token is an httpOnly cookie; without this the browser
        // would not attach it to /auth/refresh.
        credentials: "include",
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (error) {
      // An aborted request is the caller changing its mind, not a failure.
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }

      throw networkError();
    }
  }

  /**
   * Single-flight refresh.
   *
   * Everyone who arrives while one is running waits for that one. Resolves to
   * null when the session is gone for good, after clearing local state so the
   * UI can react once instead of each caller deciding on its own.
   */
  private refreshOnce(): Promise<Session | null> {
    this.refreshInFlight ??= this.performRefresh().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async performRefresh(): Promise<Session | null> {
    let response: Response;

    try {
      response = await this.dispatch("POST", "/auth/refresh", {}, false);
    } catch {
      // A network blip is not proof the session ended, so the token in memory
      // stays and the original call surfaces the failure instead.
      return null;
    }

    if (!response.ok) {
      this.setSession(null);

      return null;
    }

    try {
      const session = loginResponseSchema.parse(await response.json());

      this.setSession(session);

      return session;
    } catch {
      this.setSession(null);

      return null;
    }
  }

  private async parse<TResult>(
    response: Response,
    schema: z.ZodType<TResult> | undefined,
  ): Promise<TResult> {
    if (!response.ok) {
      throw await toApiError(response);
    }

    if (response.status === 204) {
      return undefined as TResult;
    }

    const payload: unknown = await response.json();

    if (!schema) {
      return payload as TResult;
    }

    const parsed = schema.safeParse(payload);

    if (!parsed.success) {
      // The server and this client disagree on the contract. Saying so beats
      // rendering a half-empty screen that looks like missing data.
      throw new ApiError({
        status: response.status,
        code: "INTERNAL_ERROR",
        message: "The server response is not in the expected format.",
      });
    }

    return parsed.data;
  }

  private url(path: string, query: RequestOptions<unknown>["query"]): string {
    const search = new URLSearchParams();

    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined && value !== "") {
        search.set(key, String(value));
      }
    }

    const suffix = search.size > 0 ? `?${search.toString()}` : "";

    return `${this.baseUrl}${path}${suffix}`;
  }
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }

  const plain = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  return plain?.[1]?.trim() ?? null;
}

/** The instance the application uses. Tests build their own. */
export const apiClient = new ApiClient();
